<template>
  <Loading v-if="props.loading" :data-loading="props.loading" />
  <div v-else class="card-chart-container" :data-loading="props.loading">
    <VueApexCharts
      :options="chart.chartOptions"
      :series="chart.series"
      width="100%"
      height="100%"
    />
  </div>
</template>

<script setup lang="ts">
import { formatNumber } from "@/utils";
import _ from "lodash";
import VueApexCharts from "vue3-apexcharts";
import Loading from "./../Widgets/Loading.vue";
import { computed } from "vue";
import type { ApexOptions } from "apexcharts";

export interface GroupedStackedChartProps {
  series: ApexAxisChartSeries;
  labels: Array<string>;
  colors: Array<string>;
  loading?: boolean;
  title: string;
  dataLabelFormatter?: (
    val: number,
    ops: { dataPointIndex: number; seriesIndex: number }
  ) => string;
  dataLabelEnabled?: boolean;
  enabledOnSeries?: number[];
  showLegend?: boolean;
  showTotal?: boolean;
}

const props = defineProps<GroupedStackedChartProps>();

const chart = computed(() => ({
  series: _.map(props.series, (item) => ({
    name: item.name,
    group: item.group,
    data: item.data
  })),
  chartOptions: {
    chart: {
      type: "bar",
      stacked: true,
      zoom: {
        enabled: false
      },
      fontFamily: "RobotoCustom"
    },
    title: {
      text: props.title,
      style: {
        fontFamily: "RobotoCustomSemiBold",
        fontWeight: 600,
        fontSize: "15px"
      }
    },
    stroke: {
      width: 0,
      colors: ["#fff"]
    },
    dataLabels: {
      enabled: props.dataLabelEnabled ?? false,
      formatter(val, opts) {
        if (!_.isNumber(val)) return undefined;
        return props.dataLabelFormatter
          ? props.dataLabelFormatter(val, opts)
          : formatNumber(Number(val), 1);
      },
      background: {
        enabled: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: {
          total: {
            enabled: props.showTotal ?? true,
            style: {
              fontSize: "12px",
              fontWeight: 900
            },
            formatter:
              props.dataLabelFormatter ??
              function (v) {
                return formatNumber(Number(v), 1);
              }
          }
        }
      }
    },
    fill: {
      opacity: 1
    },
    colors: props.colors,
    xaxis: {
      categories: props.labels
    },
    yaxis: {
      labels: {
        formatter(val, { dataPointIndex, seriesIndex }) {
          if (dataPointIndex !== undefined && dataPointIndex !== -1 && seriesIndex !== undefined) {
            const label = (props.series[seriesIndex].data[dataPointIndex] as any)?.meta?.label;
            return `<span class="text-normal">${label ?? props.labels[dataPointIndex]} :</span> ${formatNumber(Number(val))}`;
          }
          return `${formatNumber(Number(val))}`;
        }
      }
    },
    tooltip: {
      x: {
        formatter(val, { dataPointIndex, seriesIndex }) {
          return (
            (props.series[seriesIndex].data[dataPointIndex] as any)?.x ??
            props.labels?.[dataPointIndex] ??
            val
          );
        }
      },
      followCursor: true,
      enabled: true
    },
    legend: {
      show: props.showLegend ?? true,
      position: "top",
      horizontalAlign: "left"
    }
  } as ApexOptions
}));
</script>

<style>
.apexcharts-tooltip-text > .apexcharts-tooltip-y-group {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
